/**
 * LegalCitation - Clickable legal reference link
 *
 * Displays a short legal citation (e.g., "DüV § 4 Abs. 4") that can be clicked
 * to show the full legal text in a side panel.
 */

import React from 'react';
import { Scale } from 'lucide-react';
import './LegalCitation.css';

export interface LegalCitationProps {
  /** The short citation text (e.g., "DüV § 4 Abs. 4") */
  citation: string;

  /** Optional law full name for tooltip */
  lawFullName?: string;

  /** Click handler when citation is clicked */
  onClick?: () => void;

  /** Visual variant */
  variant?: 'default' | 'inline' | 'compact';

  /** Whether to show the scale icon */
  showIcon?: boolean;

  /** Additional CSS class */
  className?: string;
}

export const LegalCitation: React.FC<LegalCitationProps> = ({
  citation,
  lawFullName,
  onClick,
  variant = 'default',
  showIcon = true,
  className = '',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <button
      type="button"
      className={`legal-citation legal-citation--${variant} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      title={lawFullName}
      aria-label={`${citation}${lawFullName ? ` (${lawFullName})` : ''} - Klicken für Details`}
    >
      {showIcon && variant !== 'compact' && (
        <Scale className="legal-citation__icon" aria-hidden="true" />
      )}
      <span className="legal-citation__text">{citation}</span>
    </button>
  );
};

export default LegalCitation;
